import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { SHOW_MENU_PRICES } from '../config/ui.ts';

const Dashboard = styled.div`
  min-height: 100vh;
  background: var(--white);
`;

const Header = styled.header`
  background: var(--navy-blue);
  color: var(--white);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

const Logo = styled.h1`
  margin: 0;
  font-size: 1.5rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const MainContent = styled.main`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: var(--white);
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  h3 {
    margin: 0 0 0.5rem 0;
    color: #666;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
  
  .value {
    font-size: 2rem;
    font-weight: bold;
    color: var(--navy-blue);
  }
`;

const TablesSection = styled.section`
  background: var(--white);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  h2 {
    margin: 0;
    color: var(--navy-blue);
  }
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  background: ${props => props.active ? 'var(--primary-turquoise)' : '#e0e0e0'};
  color: ${props => props.active ? 'var(--white)' : '#666'};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-turquoise)' : '#d0d0d0'};
  }
`;

const TablesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const TableCard = styled.div<{ status: string }>`
  background: var(--white);
  border: 2px solid ${props => 
    props.status === 'occupied' ? '#ff6b6b' :
    props.status === 'pending' ? '#feca57' :
    '#1dd1a1'
  };
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  h3 {
    margin: 0;
    font-size: 1.3rem;
  }
`;

const TableStatus = styled.span<{ status: string }>`
  background: ${props => 
    props.status === 'occupied' ? '#ff6b6b' :
    props.status === 'pending' ? '#feca57' :
    '#1dd1a1'
  };
  color: var(--white);
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const OrderInfo = styled.div`
  .order-item {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px solid #e0e0e0;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .item-name {
    font-weight: 500;
  }
  
  .item-quantity {
    color: #666;
  }
`;

const TableFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
  
  .total {
    font-weight: bold;
    font-size: 1.1rem;
  }
`;

const ActionButton = styled.button`
  background: var(--primary-turquoise);
  color: var(--white);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2db3a3;
  }
`;

// Mock data para demostración
const mockTables = [
  {
    id: 1,
    number: '01',
    status: 'occupied',
    customerName: 'Juan Pérez',
    orders: [
      { id: 1, name: 'Ceviche Mixto', quantity: 2, price: 35 },
      { id: 2, name: 'Chicharrón de Pescado', quantity: 1, price: 28 }
    ],
    startTime: new Date(Date.now() - 25 * 60000)
  },
  {
    id: 2,
    number: '02',
    status: 'pending',
    customerName: 'María García',
    orders: [
      { id: 3, name: 'Arroz con Mariscos', quantity: 1, price: 42 }
    ],
    startTime: new Date(Date.now() - 10 * 60000)
  },
  {
    id: 3,
    number: '03',
    status: 'available',
    customerName: null,
    orders: [],
    startTime: null
  },
  {
    id: 4,
    number: '04',
    status: 'occupied',
    customerName: 'Carlos López',
    orders: [
      { id: 4, name: 'Parihuela', quantity: 2, price: 38 },
      { id: 5, name: 'Leche de Tigre', quantity: 2, price: 15 }
    ],
    startTime: new Date(Date.now() - 45 * 60000)
  }
];

const WaiterDashboard: React.FC = () => {
  const [tables] = useState(mockTables);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    // Animación de entrada
    gsap.fromTo('.stat-card', 
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.5, stagger: 0.1 }
    );
    
    gsap.fromTo('.table-card',
      { opacity: 0, scale: 0.9 },
      { opacity: 1, scale: 1, duration: 0.5, stagger: 0.1 }
    );

    // Simulación de actualizaciones en tiempo real
    const interval = setInterval(() => {
      // Aquí iría la lógica de WebSocket para actualizar pedidos
      console.log('Checking for updates...');
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getFilteredTables = () => {
    if (filter === 'all') return tables;
    return tables.filter(table => table.status === filter);
  };

  const getTotalOrders = () => {
    return tables.reduce((total, table) => total + table.orders.length, 0);
  };

  const getOccupiedTables = () => {
    return tables.filter(table => table.status === 'occupied').length;
  };

  const getTimeElapsed = (startTime: Date | null) => {
    if (!startTime) return '';
    const diff = Date.now() - startTime.getTime();
    const minutes = Math.floor(diff / 60000);
    return `${minutes} min`;
  };

  const getTotalPrice = (orders: any[]) => {
    return orders.reduce((total, order) => total + (order.price * order.quantity), 0);
  };

  const handleTableAction = (tableId: number) => {
    // Aquí iría la lógica para marcar como completado, etc.
    console.log('Action for table:', tableId);
  };

  return (
    <Dashboard>
      <Header>
        <Logo>Pepe Tiburón - Panel de Mozos</Logo>
        <UserInfo>
          <span>Hola, Carlos</span>
          <button>Cerrar Sesión</button>
        </UserInfo>
      </Header>

      <MainContent>
        <StatsRow>
          <StatCard className="stat-card">
            <h3>Mesas Ocupadas</h3>
            <div className="value">{getOccupiedTables()} / {tables.length}</div>
          </StatCard>
          
          <StatCard className="stat-card">
            <h3>Pedidos Activos</h3>
            <div className="value">{getTotalOrders()}</div>
          </StatCard>
          
          <StatCard className="stat-card">
            <h3>Pedidos Pendientes</h3>
            <div className="value">{tables.filter(t => t.status === 'pending').length}</div>
          </StatCard>
          
          <StatCard className="stat-card">
            <h3>Tiempo Promedio</h3>
            <div className="value">28 min</div>
          </StatCard>
        </StatsRow>

        <TablesSection>
          <SectionHeader>
            <h2>Mesas</h2>
            <FilterButtons>
              <FilterButton 
                active={filter === 'all'}
                onClick={() => setFilter('all')}
              >
                Todas
              </FilterButton>
              <FilterButton 
                active={filter === 'occupied'}
                onClick={() => setFilter('occupied')}
              >
                Ocupadas
              </FilterButton>
              <FilterButton 
                active={filter === 'pending'}
                onClick={() => setFilter('pending')}
              >
                Pendientes
              </FilterButton>
              <FilterButton 
                active={filter === 'available'}
                onClick={() => setFilter('available')}
              >
                Disponibles
              </FilterButton>
            </FilterButtons>
          </SectionHeader>

          <TablesGrid>
            {getFilteredTables().map(table => (
              <TableCard 
                key={table.id}
                status={table.status}
                className="table-card"
                onClick={() => handleTableAction(table.id)}
              >
                <TableHeader>
                  <h3>Mesa {table.number}</h3>
                  <TableStatus status={table.status}>
                    {table.status === 'occupied' ? 'Ocupada' :
                     table.status === 'pending' ? 'Pendiente' :
                     'Disponible'}
                  </TableStatus>
                </TableHeader>

                {table.status !== 'available' && (
                  <>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>{table.customerName}</strong>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>
                        {getTimeElapsed(table.startTime)}
                      </div>
                    </div>

                    <OrderInfo>
                      {table.orders.map(order => (
                        <div key={order.id} className="order-item">
                          <span className="item-name">{order.name}</span>
                          <span className="item-quantity">x{order.quantity}</span>
                        </div>
                      ))}
                    </OrderInfo>

                    <TableFooter>
                      <div className="total">
                        Total: {SHOW_MENU_PRICES ? `S/. ${getTotalPrice(table.orders).toFixed(2)}` : 'Consultar'}
                      </div>
                      <ActionButton onClick={() => handleTableAction(table.id)}>
                        {table.status === 'pending' ? 'Marcar Listo' : 'Ver Detalles'}
                      </ActionButton>
                    </TableFooter>
                  </>
                )}
              </TableCard>
            ))}
          </TablesGrid>
        </TablesSection>
      </MainContent>
    </Dashboard>
  );
};

export default WaiterDashboard;